/**
 * Copyright (c) 2022 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { MouseEventHandler } from 'react';
import { Box, Text } from 'grommet';
import { ActionButton } from '@blend/enterprise-components';
import { UserCredentialType } from 'blb-constants/ron';
import { styledAttrs } from 'blb-utils/misc';
import { EditCredentialFormValues } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/types';
import { CredentialAttributeInputFields } from 'blb-components/AuthenticatedApp/RON/CredentialInfo/CredentialAttributeInputFields';

const ActionButtonWrapper = styledAttrs(Box, {
  border: { color: 'cloud70', side: 'top', size: 'xsmall' },
  direction: 'row',
  gap: 'small',
  justify: 'end',
  pad: { horizontal: 'medium', vertical: 'small' },
});

const HeaderWrapper = styledAttrs(Box, {
  border: { color: 'cloud70', side: 'bottom', size: 'xsmall' },
  flex: false,
  pad: { bottom: 'small', left: 'medium', right: 'medium', top: 'medium' },
});

export namespace AttributeEditor {
  export interface Props {
    credentialType: UserCredentialType;
    formValues: EditCredentialFormValues;
    handleSubmit: MouseEventHandler;
    hasValidationErrors: boolean;
    isDirty: boolean;
    isSavingChanges: boolean;
    title: string;
    uploadLink: React.ReactElement;
  }
}

export const AttributeEditor: React.FC<AttributeEditor.Props> = ({
  credentialType,
  formValues,
  handleSubmit,
  hasValidationErrors,
  isSavingChanges,
  isDirty,
  title,
  uploadLink,
}): React.ReactElement => {
  const fileName = formValues.fileName;
  return (
    <>
      <HeaderWrapper>
        <Text size="18px" weight="bold">
          {title}
        </Text>
        <Box direction="row" justify="between" margin={{ top: 'xsmall' }}>
          {fileName && (
            <Text id="it-uploadedFileName" size="small">
              {fileName}
            </Text>
          )}
          {uploadLink}
        </Box>
      </HeaderWrapper>

      <Box flex="grow" pad="medium">
        <CredentialAttributeInputFields credentialType={credentialType} />
      </Box>

      <ActionButtonWrapper>
        <ActionButton
          disabled={hasValidationErrors || !isDirty}
          id="it-saveChangeButton"
          isLoading={isSavingChanges}
          label="Save changes"
          onClick={handleSubmit}
        />
      </ActionButtonWrapper>
    </>
  );
};
